/**
 * Comparación ilustrativa contra "una app" de delivery (spec 2026-09-03 §5).
 *
 * SOLO PARA MOSTRAR. Nada de esto entra en un cobro: el envío real es
 * `heldQuote.feeAmountCents` y se pasa aquí tal cual, solo para decidir qué
 * tabla se muestra. Las cifras "de la app" son típicas de la industria,
 * aproximadas, y viven aquí con su fuente y fecha: cambiar una es tocar un
 * número, no buscarlo por la UI.
 */

/** Menú 15–25% más caro en las apps para cubrir la comisión (Norvet MSP; Sauce, sep-2026). Se toma el medio. */
export const APP_MENU_MARKUP_PCT = 20
/** Tarifa de servicio típica 10–15% del subtotal (DoorDash, "Updating our fee structure", jul-2026). Techo. */
export const APP_SERVICE_FEE_PCT = 15
/** Mínimo habitual de la tarifa de servicio, ~$3 (Ridesharing Driver, "All about DoorDash prices"; splitty, "DoorDash fees explained" — sep-2026). */
export const APP_SERVICE_FEE_MIN_CENTS = 300
/** Umbral del recargo por pedido chico: pedidos bajo $12 (Ridesharing Driver; splitty — sep-2026). */
export const APP_SMALL_ORDER_THRESHOLD_CENTS = 1200
/** Recargo por pedido chico, $2 (Ridesharing Driver; splitty — sep-2026). */
export const APP_SMALL_ORDER_FEE_CENTS = 200
/** Envío en app $0.99–$7.99 según distancia, demanda y nivel del restaurante (Ridesharing Driver; Sauce, "How much does DoorDash cost" — sep-2026). $3.99 es la referencia media que se toma. */
export const APP_DELIVERY_FEE_CENTS = 399
/** No es una cifra de mercado: decisión de diseño del spec (§4, "Sin carrito") — la tabla se explica con un pedido de $30. */
export const EXAMPLE_FOOD_CENTS = 3000

export type ComparisonRowKey = 'food' | 'service' | 'smallOrder' | 'delivery' | 'total'

export interface ComparisonRow {
  key: ComparisonRowKey
  appCents: number
  hereCents: number
}

export interface AppComparison {
  /** `full` = con envío, total y ahorro; `compact` = solo comida + tarifas. */
  mode: 'full' | 'compact'
  /** true cuando no había comida y se usó EXAMPLE_FOOD_CENTS. */
  example: boolean
  foodCents: number
  /** El envío real, verbatim; null cuando no hay cotización viva. */
  deliveryFeeCents: number | null
  rows: ComparisonRow[]
  /** Solo en `full`; siempre > 0. */
  savingsCents: number | null
}

export function compareWithApps(input: { foodCents: number; deliveryFeeCents: number | null }): AppComparison {
  const example = !(Number.isFinite(input.foodCents) && input.foodCents > 0)
  const foodCents = example ? EXAMPLE_FOOD_CENTS : Math.round(input.foodCents)
  const deliveryFeeCents =
    input.deliveryFeeCents == null || !Number.isFinite(input.deliveryFeeCents)
      ? null
      : Math.max(0, Math.round(input.deliveryFeeCents))

  const appFood = Math.round(foodCents * (1 + APP_MENU_MARKUP_PCT / 100))
  const appService = Math.max(Math.round((appFood * APP_SERVICE_FEE_PCT) / 100), APP_SERVICE_FEE_MIN_CENTS)
  const appSmall = foodCents < APP_SMALL_ORDER_THRESHOLD_CENTS ? APP_SMALL_ORDER_FEE_CENTS : 0

  const rows: ComparisonRow[] = [
    { key: 'food', appCents: appFood, hereCents: foodCents },
    { key: 'service', appCents: appService, hereCents: 0 },
  ]
  if (appSmall > 0) rows.push({ key: 'smallOrder', appCents: appSmall, hereCents: 0 })

  if (deliveryFeeCents == null) {
    return { mode: 'compact', example, foodCents, deliveryFeeCents: null, rows, savingsCents: null }
  }

  const appTotal = appFood + appService + appSmall + APP_DELIVERY_FEE_CENTS
  const hereTotal = foodCents + deliveryFeeCents
  const savings = appTotal - hereTotal
  if (savings <= 0) {
    return { mode: 'compact', example, foodCents, deliveryFeeCents, rows, savingsCents: null }
  }
  rows.push({ key: 'delivery', appCents: APP_DELIVERY_FEE_CENTS, hereCents: deliveryFeeCents })
  rows.push({ key: 'total', appCents: appTotal, hereCents: hereTotal })
  return { mode: 'full', example, foodCents, deliveryFeeCents, rows, savingsCents: savings }
}
